import React, { useEffect, useRef } from "react";
import AuthedImage from "../editor/AuthedImage";
import { CALLOUT_VARIANTS, DEFAULT_BOOK_STYLE_TOKENS } from "./chapterBlocks";

function BlockImage({ url, alt }) {
  if (!url) return null;
  if (String(url).startsWith("/api/")) {
    return <AuthedImage path={url} alt={alt || ""} />;
  }
  return <img src={url} alt={alt || ""} />;
}

function mergeStyle(bookTokens, blockType, blockStyle = {}) {
  const base = bookTokens?.[blockType] || DEFAULT_BOOK_STYLE_TOKENS[blockType] || {};
  const body = bookTokens?.body || DEFAULT_BOOK_STYLE_TOKENS.body;
  if (["paragraph", "bullet_list", "numbered_list", "checklist", "caption"].includes(blockType)) {
    return { ...body, ...base, ...blockStyle };
  }
  return { ...base, ...blockStyle };
}

function styleToCss(s = {}) {
  return {
    fontFamily: s.fontFamily,
    fontSize: s.fontSize ? `${s.fontSize}px` : undefined,
    fontWeight: s.fontWeight,
    fontStyle: s.fontStyle,
    lineHeight: s.lineHeight,
    color: s.color,
    textAlign: s.alignment,
    letterSpacing: s.letterSpacing != null ? `${s.letterSpacing}em` : undefined,
    marginTop: s.spacingAbove != null ? `${s.spacingAbove}px` : undefined,
    marginBottom: s.spacingBelow != null ? `${s.spacingBelow}px` : (s.paragraphSpacing != null ? `${s.paragraphSpacing}px` : undefined),
    textIndent: s.firstLineIndent != null ? `${s.firstLineIndent}px` : undefined,
    background: s.background,
    borderLeft: s.borderColor ? `${s.borderWidth || 4}px solid ${s.borderColor}` : undefined,
    padding: s.padding != null ? `${s.padding}px` : undefined,
    borderRadius: s.cornerRadius != null ? `${s.cornerRadius}px` : undefined,
  };
}

function EditableText({ value, onChange, readOnly, className, placeholder, multiline = true }) {
  if (readOnly) {
    return <span className={className}>{value || placeholder || ""}</span>;
  }
  if (!multiline) {
    return (
      <input
        className={`bl-block-input ${className || ""}`}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }
  return (
    <textarea
      className={`bl-block-textarea ${className || ""}`}
      value={value || ""}
      placeholder={placeholder}
      rows={Math.max(2, String(value || "").split("\n").length)}
      onChange={(e) => onChange?.(e.target.value)}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function DiagramView({ content, onChangeLabel, readOnly }) {
  const nodes = content?.nodes || [];
  const type = content?.diagramType || "roadmap";
  return (
    <div className={`bl-diagram bl-diagram--${type}`} data-testid="diagram-block">
      <div className="bl-diagram-title">{content?.title || "Diagram"}</div>
      <ol className="bl-diagram-nodes">
        {nodes.map((node, i) => (
          <li key={i} className="bl-diagram-node">
            <span className="bl-diagram-step">{i + 1}</span>
            {readOnly ? (
              <span>{node}</span>
            ) : (
              <input
                className="bl-block-input"
                value={node}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const next = [...nodes];
                  next[i] = e.target.value;
                  onChangeLabel?.(next);
                }}
              />
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function BlockView({
  block,
  bookTokens,
  selected,
  onSelect,
  onChange,
  readOnly,
  scrollToId,
}) {
  const ref = useRef(null);
  useEffect(() => {
    if (
      scrollToId &&
      scrollToId === block.id &&
      ref.current &&
      typeof ref.current.scrollIntoView === "function"
    ) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [scrollToId, block.id]);

  const s = mergeStyle(bookTokens, block.type, block.style);
  const c = block.content || {};
  const css = styleToCss(s);

  const updateContent = (patch) => onChange?.({ content: patch });

  let body = null;
  switch (block.type) {
    case "chapter_title":
      body = <EditableText className="bl-b-title" value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Chapter title" />;
      break;
    case "subtitle":
      body = <EditableText className="bl-b-subtitle" value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Subtitle" />;
      break;
    case "heading_1":
      body = <EditableText className="bl-b-h1" value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Heading" multiline={false} />;
      break;
    case "heading_2":
      body = <EditableText className="bl-b-h2" value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Subheading" multiline={false} />;
      break;
    case "heading_3":
      body = <EditableText className="bl-b-h3" value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Section" multiline={false} />;
      break;
    case "paragraph":
      body = <EditableText className="bl-b-p" value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Start writing…" />;
      break;
    case "quote":
      body = (
        <blockquote className="bl-b-quote">
          <EditableText value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Quote" />
          {(c.attribution || !readOnly) && (
            <cite>
              <EditableText value={c.attribution} onChange={(t) => updateContent({ attribution: t })} readOnly={readOnly} placeholder="Attribution" multiline={false} />
            </cite>
          )}
        </blockquote>
      );
      break;
    case "callout": {
      const variant = CALLOUT_VARIANTS.find((v) => v.id === (s.variant || c.variant || "information")) || CALLOUT_VARIANTS[5];
      body = (
        <div
          className="bl-b-callout"
          style={{
            background: s.background || variant.background,
            borderLeft: `${s.borderWidth || 4}px solid ${s.borderColor || variant.borderColor}`,
            borderRadius: s.cornerRadius ?? 10,
            padding: s.padding ?? 14,
          }}
        >
          <div className="bl-b-callout-label">
            <EditableText value={c.label || variant.label} onChange={(t) => updateContent({ label: t })} readOnly={readOnly} multiline={false} />
          </div>
          <EditableText value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Callout text" />
        </div>
      );
      break;
    }
    case "bullet_list":
    case "numbered_list":
    case "checklist": {
      const Tag = block.type === "numbered_list" ? "ol" : "ul";
      const items = c.items || [""];
      body = (
        <Tag className={`bl-b-list bl-b-list--${block.type}`}>
          {items.map((item, i) => (
            <li key={i}>
              {block.type === "checklist" && <input type="checkbox" disabled={readOnly} readOnly />}
              <EditableText
                value={typeof item === "string" ? item : item.text}
                onChange={(t) => {
                  const next = [...items];
                  next[i] = t;
                  updateContent({ items: next });
                }}
                readOnly={readOnly}
                placeholder="List item"
                multiline={false}
              />
            </li>
          ))}
        </Tag>
      );
      break;
    }
    case "table": {
      const rows = c.rows || [["Column A", "Column B"], ["", ""]];
      const ts = { ...DEFAULT_BOOK_STYLE_TOKENS.table, ...(bookTokens?.table || {}), ...s };
      body = (
        <div className="bl-b-table-wrap">
          <table className="bl-b-table" style={{ fontSize: ts.fontSize }}>
            <tbody>
              {rows.map((row, ri) => (
                <tr
                  key={ri}
                  style={{
                    background: ri === 0 ? ts.headerBackground : ri % 2 === 0 ? ts.alternateRow : "transparent",
                    color: ri === 0 ? ts.headerTextColor : undefined,
                  }}
                >
                  {(row || []).map((cell, ci) => (
                    <td key={ci} style={{ padding: ts.cellPadding, borderColor: ts.borderColor }}>
                      <EditableText
                        value={cell}
                        onChange={(t) => {
                          const next = rows.map((r) => [...r]);
                          next[ri][ci] = t;
                          updateContent({ rows: next });
                        }}
                        readOnly={readOnly}
                        multiline={false}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      break;
    }
    case "image":
      body = (
        <figure className={`bl-b-image bl-b-image--${c.placement || "inline"}`} data-testid="image-block">
          {c.url ? (
            <BlockImage url={c.url} alt={c.alt || c.caption || ""} />
          ) : (
            <div className="bl-b-image-placeholder">
              <strong>Image planned</strong>
              <p>{c.prompt || c.caption || "No image yet — use the Images panel to generate or upload."}</p>
            </div>
          )}
          {(c.caption || !readOnly) && (
            <figcaption>
              <EditableText value={c.caption} onChange={(t) => updateContent({ caption: t })} readOnly={readOnly} placeholder="Caption" multiline={false} />
            </figcaption>
          )}
        </figure>
      );
      break;
    case "caption":
      body = <EditableText className="bl-b-caption" value={c.text} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} placeholder="Caption" />;
      break;
    case "divider":
      body = <hr className="bl-b-divider" />;
      break;
    case "page_break":
      body = <div className="bl-b-pagebreak" data-testid="page-break">Page break</div>;
      break;
    case "diagram":
      body = (
        <DiagramView
          content={c}
          readOnly={readOnly}
          onChangeLabel={(nodes) => updateContent({ nodes })}
        />
      );
      break;
    default:
      body = <EditableText value={c.text || ""} onChange={(t) => updateContent({ text: t })} readOnly={readOnly} />;
  }

  return (
    <div
      ref={ref}
      id={`block-${block.id}`}
      className={`bl-block${selected ? " is-selected" : ""}`}
      data-block-id={block.id}
      data-block-type={block.type}
      data-testid={`block-${block.type}`}
      style={["callout", "image", "diagram", "table", "divider", "page_break"].includes(block.type) ? undefined : css}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(block.id);
      }}
    >
      {body}
    </div>
  );
}

/**
 * Center book-page editor.
 * pageView: paper | dark | match
 * editorMode: write | design | preview
 */
export default function ChapterPageEditor({
  blocks = [],
  bookTokens = DEFAULT_BOOK_STYLE_TOKENS,
  selectedBlockId,
  onSelectBlock,
  onChangeBlock,
  pageView = "paper",
  editorMode = "write",
  scrollToBlockId,
  headerConfig,
  footerConfig,
  chapterTitle,
  bookTitle,
  pageNumber,
}) {
  const readOnly = editorMode === "preview";
  const viewTheme = pageView === "match"
    ? (typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "paper")
    : pageView;

  return (
    <article
      className="book-page"
      data-view-theme={viewTheme === "dark" ? "dark" : "paper"}
      data-editor-mode={editorMode}
      data-testid="book-page"
      onClick={() => onSelectBlock?.(null)}
    >
      {headerConfig?.enabled !== false && (
        <header className="book-page-header" data-testid="book-page-header">
          <span>{headerConfig?.left || bookTitle || ""}</span>
          <span>{headerConfig?.right || chapterTitle || ""}</span>
        </header>
      )}
      <div className="book-page-body">
        {blocks.map((block) => (
          <BlockView
            key={block.id}
            block={block}
            bookTokens={bookTokens}
            selected={selectedBlockId === block.id}
            onSelect={onSelectBlock}
            onChange={(patch) => onChangeBlock?.(block.id, patch)}
            readOnly={readOnly}
            scrollToId={scrollToBlockId}
          />
        ))}
        {!blocks.length && (
          <p className="bl-empty-page">This chapter has no content yet. Generate it or start typing in a new paragraph.</p>
        )}
      </div>
      {footerConfig?.enabled !== false && (
        <footer className="book-page-footer" data-testid="book-page-footer">
          <span>{footerConfig?.text || footerConfig?.disclaimer || ""}</span>
          <span className="book-page-num">{pageNumber ?? ""}</span>
        </footer>
      )}
    </article>
  );
}
